import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { DecisionDetail, Ordre } from '@hector/shared-types';

// Re-export pour les consumers existants (DecisionViewPage) qui importent le type depuis ce module.
export type { DecisionDetail, Ordre };

/**
 * Lit une décision du corpus interne pour le viewer interne.
 *
 * Route : `GET /api/decisions/:ordre/:id`
 * - `ordre` : 'judiciaire' | 'administratif' (OrdreSchema — valeurs stables, pas 'jp'/'admin')
 *
 * WHY `staleTime: Infinity` : le corpus jurisprudentiel est immuable (une décision
 * publiée ne change pas) — inutile de re-fetcher. Pas de scope cabinet : corpus global.
 *
 * WHY types depuis @hector/shared-types : `DecisionDetail` est la forme Zod-validée
 * par le backend avant envoi — shared-types est la SSOT pour ce contrat.
 */
export function useDecision(ordre: Ordre | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['decision', ordre, id],
    enabled: !!ordre && !!id,
    staleTime: Infinity,
    queryFn: async () => {
      // WHY pas de double-déballage : `apiClient.get` retourne déjà `result.data`
      // (cf. apiClient.ts — `return result.data || result`). La route renvoie
      // `{ success, data: DecisionDetail }`, donc `apiClient.get` rend directement
      // le `DecisionDetail`. Re-lire `.data` ici donnait `undefined` → viewer vide.
      return apiClient.get<DecisionDetail>(`/decisions/${ordre}/${id}`);
    },
  });
}
